import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Modal } from 'react-responsive-modal';
import 'react-responsive-modal/styles.css';
import { ReactComponent as CloseIcon } from "../images/icons/white-close.svg";
import { ReactComponent as UploadIcon } from "../images/icons/upload.svg";
import { useGenerateDispatch, useGenerateStatus } from "../context/GenerateContext";
import { usePromptusAPIState } from "../context/PromptusAPIContext";
import { useAccountContext } from "../context/AccountContext";
import { generatePrompt, getTemplateCategories, getTemplateList, getTemplate, getApiBaseUrl, getAuthorizationToken } from "../api-helper";
import axios from 'axios';
import Swal from 'sweetalert2';
import electronBridge from '../utils/electronBridge';
import { db, extractCosyflowMeta } from '../db';
import md5 from 'blueimp-md5';
import eventBus from '../utils/eventBus';

// Import template images
import PortraitProImg from "../images/templates/portrait-pro.svg";
import LandscapeNatureImg from "../images/templates/landscape-nature.svg";
import LogoDesignImg from "../images/templates/logo-design.svg";
import ProductShotImg from "../images/templates/product-shot.svg";
import FantasyArtImg from "../images/templates/fantasy-art.svg";
import AnimeCharacterImg from "../images/templates/anime-character.svg";
import ArchitecturalImg from "../images/templates/architectural.svg";
import AbstractArtImg from "../images/templates/abstract-art.svg";
import PModal from "../components/Modal.jsx"
import LoginModal from "../components/LoginModal.jsx";


// Template data with predefined models and parameters
const TEMPLATES = [
    {
        id: 'portrait-pro',
        title: 'Professional Portrait',
        description: 'Create stunning professional headshots and portraits with perfect lighting and composition',
        model: '(AI-Horde) Flux.1-Schnell fp8 (Compact)',
        prompt: 'professional studio portrait photography, sharp focus, professional lighting, detailed face, high quality',
        parameters: {
            steps: 20,
            guidance: 7.5,
            imageWidth: 512,
            imageHeight: 768,
            scheduler: 'DPM-Solver',
            negativeKeywords: 'blurry, low quality, distorted, deformed'
        },
        category: 'Photography',
        imageUrl: PortraitProImg
    },
    {
        id: 'landscape-nature',
        title: 'Scenic Landscape',
        description: 'Generate breathtaking natural landscapes with vivid colors and atmospheric depth',
        model: '(cosy) Flux Dev fp8',
        prompt: 'beautiful landscape photography, dramatic lighting, vibrant colors, ultra detailed, 8k',
        parameters: {
            steps: 25,
            guidance: 8,
            imageWidth: 1024,
            imageHeight: 512,
            scheduler: 'DPM++ 2M Karras',
            negativeKeywords: 'ugly, boring, flat lighting'
        },
        category: 'Nature',
        imageUrl: LandscapeNatureImg
    },
    {
        id: 'logo-design',
        title: 'Logo & Icon Design',
        description: 'Design clean, modern logos and icons perfect for branding and UI elements',
        model: '(geminiimages) Nano Banana 2.5 Flash',
        prompt: 'modern minimalist logo design, clean lines, professional, vector style, simple',
        parameters: {
            steps: 15,
            guidance: 9,
            imageWidth: 512,
            imageHeight: 512,
            scheduler: 'Euler',
            negativeKeywords: 'complex, cluttered, messy, realistic'
        },
        category: 'Design',
        imageUrl: LogoDesignImg
    },
    {
        id: 'product-shot',
        title: 'Product Photography',
        description: 'Perfect for e-commerce: clean product shots with professional studio lighting',
        model: '(AI-Horde) Flux.1-Schnell fp8 (Compact)',
        prompt: 'product photography, studio lighting, white background, commercial shot, high detail',
        parameters: {
            steps: 18,
            guidance: 7,
            imageWidth: 768,
            imageHeight: 768,
            scheduler: 'DPM-Solver',
            negativeKeywords: 'cluttered, poor lighting, blurry'
        },
        category: 'Commercial',
        imageUrl: ProductShotImg
    },
    {
        id: 'fantasy-art',
        title: 'Fantasy Illustration',
        description: 'Create magical fantasy scenes with vibrant colors and imaginative details',
        model: '(cosy) Flux Dev fp8',
        prompt: 'fantasy illustration, magical atmosphere, detailed artwork, vibrant colors, concept art',
        parameters: {
            steps: 30,
            guidance: 9,
            imageWidth: 768,
            imageHeight: 1024,
            scheduler: 'DPM++ 2M SDE Karras',
            negativeKeywords: 'realistic, photograph, plain, boring'
        },
        category: 'Art',
        imageUrl: FantasyArtImg
    },
    {
        id: 'anime-character',
        title: 'Anime Character',
        description: 'Generate anime-style character art with clean lines and vibrant colors',
        model: '(AI-Horde) Flux.1-Schnell fp8 (Compact)',
        prompt: 'anime character, detailed illustration, vibrant colors, clean lines, high quality',
        parameters: {
            steps: 22,
            guidance: 8.5,
            imageWidth: 512,
            imageHeight: 768,
            scheduler: 'Euler a',
            negativeKeywords: 'realistic, 3d, blurry, low quality'
        },
        category: 'Art',
        imageUrl: AnimeCharacterImg
    },
    {
        id: 'architectural',
        title: 'Architecture Design',
        description: 'Modern architectural visualization with perfect perspective and lighting',
        model: '(cosy) Flux Dev fp8',
        prompt: 'architectural photography, modern building, perfect perspective, professional lighting, ultra detailed',
        parameters: {
            steps: 25,
            guidance: 8,
            imageWidth: 1024,
            imageHeight: 768,
            scheduler: 'DPM++ 2M Karras',
            negativeKeywords: 'distorted, poor perspective, low quality'
        },
        category: 'Architecture',
        imageUrl: ArchitecturalImg
    },
    {
        id: 'abstract-art',
        title: 'Abstract Art',
        description: 'Create unique abstract compositions with bold colors and organic shapes',
        model: '(geminiimages) Nano Banana 2.5 Flash',
        prompt: 'abstract art, bold colors, organic shapes, modern composition, artistic',
        parameters: {
            steps: 20,
            guidance: 7,
            imageWidth: 768,
            imageHeight: 768,
            scheduler: 'Euler',
            negativeKeywords: 'realistic, photographic, plain'
        },
        category: 'Art',
        imageUrl: AbstractArtImg
    }
];

function TemplateCard({ template, onSelect, isGenerating }) {
    return (
        <div
            className="group relative bg-dark-blue/30 rounded-xl overflow-hidden border border-white/20 hover:border-yellow transition-all duration-300 cursor-pointer transform hover:scale-[1.02] h-full flex flex-col"
            onClick={() => !isGenerating && onSelect(template)}
        >
            {/* Template Image */}
            <div className="relative overflow-hidden aspect-video flex-shrink-0">
                <img
                    src={template.imageUrl}
                    alt={template.title}
                    className="w-full h-full object-cover"
                />
                {/* Category Badge */}
                <div className="absolute top-3 right-3 bg-yellow text-dark-blue px-3 py-1 rounded-full text-xs font-bold">
                    {template.category}
                </div>
            </div>

            {/* Template Info */}
            <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-2xl font-bold text-white mb-4">{template.title}</h3>
                <p className="text-lg text-white/90 mb-6 leading-relaxed flex-grow">{template.description}</p>

                {/* Model Info */}
                <div className="flex items-center justify-between text-base mb-2 bg-dark-blue/50 p-4 rounded-lg">
                    <span className="text-cyan font-semibold">Model:</span>
                    <span className="text-white/80 truncate ml-2 font-medium">{template.model}</span>
                </div>

                {/* Credits Cost */}
                <div className="flex items-center justify-between text-base mb-6 bg-dark-blue/50 p-4 rounded-lg">
                    <span className="text-cyan font-semibold">Credits Cost:</span>
                    <span className="text-yellow font-bold text-lg">{template.credits_cost || 'N/A'}</span>
                </div>

                {/* Use Template Button */}
                <button
                    className={`w-full font-bold py-4 px-5 rounded-lg transition-all duration-200 text-lg mt-auto ${
                        isGenerating
                            ? 'bg-gray-500 cursor-not-allowed'
                            : 'bg-yellow hover:bg-yellow/90 transform group-hover:scale-105'
                    } text-dark-blue`}
                    disabled={isGenerating}
                >
                    {isGenerating ? 'Generating...' : 'Use Template'}
                </button>
            </div>

            {/* Hover Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-dark-blue/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
        </div>
    );
}

export default function Templates() {
    const navigate = useNavigate();
    const location = useLocation();
    const { templateId } = useParams();

    // Debug: Log component context on mount and route changes
    useEffect(() => {
        console.log('[TEMPLATES DEBUG] Component mounted/route changed:', {
            pathname: location.pathname,
            templateId,
            isFullPageRoute: location.pathname === '/templates' || location.pathname.startsWith('/templates/'),
            timestamp: new Date().toISOString()
        });
    }, [location.pathname, templateId]);
    const generateStatusDispatch = useGenerateDispatch();
    const generateStatus = useGenerateStatus();
    const apiState = usePromptusAPIState();
    const accountContext = useAccountContext();
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [userPrompt, setUserPrompt] = useState('');
    const [uploadedImage, setUploadedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [generatingTemplateId, setGeneratingTemplateId] = useState(null);
    const [activeJobId, setActiveJobId] = useState(null);
    const [generatedImage, setGeneratedImage] = useState(null);
    const [showImageModal, setShowImageModal] = useState(false);
    const [isCheckingForImage, setIsCheckingForImage] = useState(false);
    const [portalContainer, setPortalContainer] = useState(null);
    const fileInputRef = useRef(null);

    // API data state
    const [templates, setTemplates] = useState([]);
    const [categories, setCategories] = useState(['All']);
    const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
    const [isLoadingCategories, setIsLoadingCategories] = useState(true);
    const [fetchError, setFetchError] = useState(null);

    // Model defaults state
    const [isLoadingModelDefaults, setIsLoadingModelDefaults] = useState(false);
    const [modelDefaults, setModelDefaults] = useState(null);
    const [imageFieldName, setImageFieldName] = useState('input_image'); // Default fallback
    const [cosyflowSchema, setCosyflowSchema] = useState(null); // Store full schema for dynamic field lookup

    const [showLoginModal, setShowLoginModal] = useState(false);
    const [pendingTemplateId, setPendingTemplateId] = useState(null);

    // Fetch model defaults from cosyflows/query endpoint (same as PromptHeader)
    // MUST be defined before useEffects that use it
    const fetchModelDefaults = useCallback(async (modelName) => {
        if (!modelName) {
            console.warn('[Templates] No model name provided for fetching defaults');
            return null;
        }

        setIsLoadingModelDefaults(true);
        console.log('[Templates] Fetching model defaults for:', modelName);

        try {
            // First try to get from IndexedDB
            const matchingCosyflow = await db.cosyflows
                .where('data.title')
                .equals(modelName)
                .first();

            if (matchingCosyflow && matchingCosyflow.data) {
                console.log('[Templates] Found model in IndexedDB:', modelName);
            }

            // Always fetch fresh data from API (same as PromptHeader does)
            const apiBase = await getApiBaseUrl();
            const response = await axios.get(`${apiBase}/api/cosyflows/query?title=${encodeURIComponent(modelName)}`, {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${getAuthorizationToken()}`
                }
            });

            if (response.data.cosyflows && response.data.cosyflows.length > 0) {
                const cosyflow = response.data.cosyflows[0];
                if (cosyflow.cosyflow) {
                    const cosyflowData = typeof cosyflow.cosyflow === 'string'
                        ? JSON.parse(cosyflow.cosyflow)
                        : cosyflow.cosyflow;
                    console.log('[Templates] Successfully fetched model defaults from API');
                    console.log('[Templates] Variables:', cosyflowData.variables ? Object.keys(cosyflowData.variables).length : 0);

                    // Extract default values from the cosyflow variables
                    // Keep original keys (with dots) to preserve nesting structure
                    const defaults = {};
                    let foundImageField = null;

                    if (cosyflowData.variables) {
                        for (const [key, obj] of Object.entries(cosyflowData.variables)) {
                            if (obj && typeof obj === "object") {
                                // Check if this is an image field
                                const imageTypes = ['IMAGE', 'b64data', 'file', 'b64', 'image'];
                                if (obj.type && imageTypes.includes(obj.type)) {
                                    // Keep the original key for image field (could be "b64image_0" or "params.image")
                                    foundImageField = key;
                                    console.log('[Templates] Found image field:', key, 'with type:', obj.type);
                                }

                                // Extract default value - keep original key with dots
                                if ("default" in obj) {
                                    defaults[key] = obj.default;
                                }
                            }
                        }
                    }

                    console.log('[Templates] Extracted defaults with original keys:', defaults);
                    console.log('[Templates] Image field name:', foundImageField || 'not found (using default)');

                    // Update IndexedDB if we had a match
                    if (matchingCosyflow) {
                        await db.cosyflows.update(matchingCosyflow.id, {
                            data: cosyflowData,
                            meta: extractCosyflowMeta(cosyflow),
                            timestamp: Date.now(),
                        });
                        console.log('[Templates] Updated model in IndexedDB');
                    } else {
                        // Store new cosyflow in IndexedDB
                        await db.cosyflows.add({
                            data: cosyflowData,
                            meta: extractCosyflowMeta(cosyflow),
                            timestamp: Date.now()
                        });
                        console.log('[Templates] Added new model to IndexedDB');
                    }

                    return { defaults, imageFieldName: foundImageField, schema: cosyflowData.variables };
                }
            } else {
                console.warn('[Templates] No cosyflow data found for model:', modelName);
                return null;
            }
        } catch (error) {
            console.error('[Templates] Error fetching model defaults:', error);
            return null;
        } finally {
            setIsLoadingModelDefaults(false);
        }
    }, []); // Empty dependency array since this function doesn't depend on any props or state

    const openPromptusCreditsStore = (webPage) => {
        console.log(`Opening Promptus Credits Store Page: `, webPage);
        if (electronBridge.isElectron) {
            electronBridge.ipcRenderer.send('open-urlInExternal', `https://login.promptus.ai/auto-signin?v=${accountContext.accountDetails.verificationToken}&e=${accountContext.accountDetails.emailHash}&a=${accountContext.accountDetails.accountNo}&r=/${webPage}`)
        } else {
            window.open(`https://login.promptus.ai/auto-signin?v=${accountContext.accountDetails.verificationToken}&e=${accountContext.accountDetails.emailHash}&a=${accountContext.accountDetails.accountNo}&r=/${webPage}`, '_blank');
        }
    };

    // Fetch templates and categories on mount
    useEffect(() => {
        const fetchTemplatesData = async () => {
            try {
                setIsLoadingTemplates(true);
                const templatesData = await getTemplateList();

                // Fetch categories to map category IDs to names
                let categoryMap = {};
                try {
                    const categoriesData = await getTemplateCategories();
                    categoryMap = categoriesData.reduce((acc, cat) => {
                        acc[cat.id] = cat.template_category;
                        return acc;
                    }, {});
                } catch (err) {
                    console.warn('Could not fetch categories for mapping:', err);
                }

                // Transform API data to match expected structure
                const transformedTemplates = templatesData.map(t => ({
                    id: t.id,
                    title: t.title,
                    description: t.description || '',
                    model: t.model_name || '',
                    prompt: t.base_prompt || '',
                    parameters: {
                        steps: 20,
                        guidance: 7.5,
                        imageWidth: 512,
                        imageHeight: 512,
                        scheduler: 'DPM-Solver',
                        negativeKeywords: ''
                    },
                    category: categoryMap[t.template_category_id] || 'General',
                    imageUrl: t.image_url || PortraitProImg,
                    credits_cost: t.credits_cost,
                    has_image: t.has_image === "1" || t.has_image === 1
                }));

                setTemplates(transformedTemplates);
                setFetchError(null);
            } catch (error) {
                console.error('Failed to fetch templates:', error);
                setFetchError('Failed to load templates. Using default templates.');
                // Fallback to hardcoded templates
                setTemplates(TEMPLATES);
            } finally {
                setIsLoadingTemplates(false);
            }
        };

        const fetchCategoriesData = async () => {
            try {
                setIsLoadingCategories(true);
                const categoriesData = await getTemplateCategories();

                // Extract template_category field from objects
                const categoryNames = categoriesData.map(cat => cat.template_category);

                setCategories(['All', ...categoryNames]);
                setFetchError(null);
            } catch (error) {
                console.error('Failed to fetch categories:', error);
                setFetchError('Failed to load categories. Using default categories.');
                // Fallback to hardcoded categories
                setCategories(['All', ...new Set(TEMPLATES.map(t => t.category))]);
            } finally {
                setIsLoadingCategories(false);
            }
        };

        fetchTemplatesData();
        fetchCategoriesData();
    }, []);

    // Handle URL parameter - fetch and open specific template
    useEffect(() => {
        const fetchAndOpenTemplate = async () => {
            if (!templateId) return;

            try {
                // Fetch the specific template
                const templateData = await getTemplate(templateId);

                // Fetch categories to map category ID to name
                let categoryName = 'General';
                try {
                    const categoriesData = await getTemplateCategories();
                    const category = categoriesData.find(cat => cat.id === templateData.template_category_id);
                    if (category) {
                        categoryName = category.template_category;
                    }
                } catch (err) {
                    console.warn('Could not fetch category for template:', err);
                }

                // Transform the template data
                const transformedTemplate = {
                    id: templateData.id,
                    title: templateData.title,
                    description: templateData.description || '',
                    model: templateData.model_name || '',
                    prompt: templateData.base_prompt || '',
                    parameters: {
                        steps: 20,
                        guidance: 7.5,
                        imageWidth: 512,
                        imageHeight: 512,
                        scheduler: 'DPM-Solver',
                        negativeKeywords: ''
                    },
                    category: categoryName,
                    imageUrl: templateData.image_url || PortraitProImg,
                    credits_cost: templateData.credits_cost,
                    has_image: templateData.has_image === "1" || templateData.has_image === 1
                };

                // Open the modal with this template
                setSelectedTemplate(transformedTemplate);
                setModelDefaults(null); // Reset previous defaults
                setImageFieldName('input_image'); // Reset to default
                setCosyflowSchema(null); // Reset schema
                setIsModalOpen(true);

                // Fetch model defaults from API
                if (transformedTemplate.model) {
                    const result = await fetchModelDefaults(transformedTemplate.model);
                    if (result) {
                        setModelDefaults(result.defaults);
                        setCosyflowSchema(result.schema);
                        if (result.imageFieldName) {
                            setImageFieldName(result.imageFieldName);
                        }
                        console.log('[Templates] Model defaults loaded from URL:', result.defaults);
                        console.log('[Templates] Image field name:', result.imageFieldName || 'default');
                    } else {
                        console.warn('[Templates] Could not load model defaults from URL, using template defaults');
                        setImageFieldName('input_image'); // Reset to default on failure
                        setCosyflowSchema(null);
                    }
                }
            } catch (error) {
                console.error('Failed to fetch template from URL:', error);
                Swal.fire({
                    title: 'Template Not Found',
                    text: 'The requested template could not be loaded.',
                    icon: 'error',
                    confirmButtonText: 'OK'
                });
            }
        };

        fetchAndOpenTemplate();
    }, [templateId, fetchModelDefaults]);

    // Debug: Log modal state changes
    useEffect(() => {
        if (showImageModal) {
            console.log("Modal state changed - OPEN. Generated image:", !!generatedImage, "Has image data:", !!generatedImage?.image);
        }
    }, [showImageModal, generatedImage]);

    // Set portal container safely after mount
    useEffect(() => {
        if (typeof document !== 'undefined' && document.body) {
            setPortalContainer(document.body);
        }
    }, []);

    // Watch for job completion
    useEffect(() => {
        if (!activeJobId) {
            console.log("No active job ID");
            return;
        }

        if (!generateStatus || !generateStatus.jobs) {
            console.log("No generate status or jobs");
            return;
        }

        const job = generateStatus.jobs[activeJobId];

        // If job doesn't exist but we have an activeJobId, it likely completed and was removed
        if (!job && !isCheckingForImage) {
            console.log("Job was removed (likely completed), checking database for image...");
            setIsCheckingForImage(true);

            // Wait a moment for database save, then check
            setTimeout(() => {
                db.images
                    .where('uid')
                    .equals(activeJobId)
                    .and(img => img.image && img.image !== '')
                    .first()
                    .then(image => {
                        if (image) {
                            console.log("Found generated image after job removal:", image);
                            setGeneratedImage(image);
                            setShowImageModal(true);
                            setGeneratingTemplateId(null);
                            setActiveJobId(null);
                            setIsCheckingForImage(false);
                        } else {
                            console.log("No image found after job removal, image may still be in gallery");
                            setGeneratingTemplateId(null);
                            setActiveJobId(null);
                            setIsCheckingForImage(false);
                            Swal.fire({
                                title: 'Generation Complete',
                                text: 'Your image has been generated. Please check the main gallery.',
                                icon: 'info',
                                confirmButtonText: 'OK'
                            });
                        }
                    })
                    .catch(err => {
                        console.error("Error checking database after job removal:", err);
                        setGeneratingTemplateId(null);
                        setActiveJobId(null);
                        setIsCheckingForImage(false);
                    });
            }, 1500); // Wait 1.5 seconds for database save
            return;
        }

        if (!job) {
            console.log("Job not found for ID:", activeJobId);
            return;
        }

        console.log("Template job update - ID:", activeJobId, "Status:", job.status, "Progress:", job.progress, "Outputs count:", job.outputs?.length);

        // Check if job is complete - status could be 'completed' or 'complete'
        if ((job.status === 'completed' || job.status === 'complete' || job.status === 'done') && job.outputs && job.outputs.length > 0) {
            // Get the first generated image
            const firstImage = job.outputs[0];
            console.log("Template generation complete! Showing image:", firstImage);
            setGeneratedImage(firstImage);
            setShowImageModal(true);
            setGeneratingTemplateId(null);
            setActiveJobId(null);
            setIsCheckingForImage(false);
        }
        // If status is 'done' but outputs is empty, check database
        else if (job.status === 'done' && (!job.outputs || job.outputs.length === 0) && !isCheckingForImage) {
            console.log("Template job status 'done' with empty outputs, checking database...");

            setIsCheckingForImage(true);

            // Single check with a small delay to allow database save to complete
            setTimeout(() => {
                db.images
                    .where('uid')
                    .equals(activeJobId)
                    .and(img => img.image && img.image !== '')
                    .first()
                    .then(image => {
                        if (image) {
                            console.log("Found generated image in database after job done:", image);
                            setGeneratedImage(image);
                            setShowImageModal(true);
                            setGeneratingTemplateId(null);
                            setActiveJobId(null);
                            setIsCheckingForImage(false);
                        } else {
                            console.log("No image found even after job done for job:", activeJobId);
                            setGeneratingTemplateId(null);
                            setActiveJobId(null);
                            setIsCheckingForImage(false);
                            Swal.fire({
                                title: 'Generation Issue',
                                text: 'Image was generated but could not be retrieved. Please check the main gallery.',
                                icon: 'warning',
                                confirmButtonText: 'OK'
                            });
                        }
                    })
                    .catch(err => {
                        console.error("Error fetching image from database after job done:", err);
                        setGeneratingTemplateId(null);
                        setActiveJobId(null);
                        setIsCheckingForImage(false);
                    });
            }, 1000); // Wait 1 second for database save to complete
        }
        // Check if progress is at 100 even if status is error (handles ConstraintError case)
        else if (job.progress >= 99 && (job.status === 'error' || job.status === 'generating') && !isCheckingForImage) {
            console.log("Template job at 100% progress, checking database for images...");

            // Set flag to prevent multiple simultaneous checks
            setIsCheckingForImage(true);

            // Retry mechanism - wait for database save to complete
            const checkForImage = (attempt = 0, maxAttempts = 15) => {
                db.images
                    .where('uid')
                    .equals(activeJobId)
                    .and(img => img.image && img.image !== '')
                    .first()
                    .then(image => {
                        if (image) {
                            console.log("Found generated image in database:", image);
                            console.log("Setting modal state - showImageModal: true, generatedImage:", !!image);
                            setGeneratedImage(image);
                            setShowImageModal(true);
                            setGeneratingTemplateId(null);
                            setActiveJobId(null);
                            setIsCheckingForImage(false);
                            console.log("Modal state set complete");
                        } else if (attempt < maxAttempts) {
                            console.log(`Image not found yet, retry ${attempt + 1}/${maxAttempts} in 500ms...`);
                            // Retry after 500ms
                            setTimeout(() => checkForImage(attempt + 1, maxAttempts), 500);
                        } else {
                            console.log("No image found in database after all retries for job:", activeJobId);
                            setGeneratingTemplateId(null);
                            setActiveJobId(null);
                            setIsCheckingForImage(false);
                            Swal.fire({
                                title: 'Generation Issue',
                                text: 'Image was generated but could not be retrieved. Please try again.',
                                icon: 'warning',
                                confirmButtonText: 'OK'
                            });
                        }
                    })
                    .catch(err => {
                        console.error("Error fetching image from database:", err);
                        if (attempt < maxAttempts) {
                            setTimeout(() => checkForImage(attempt + 1, maxAttempts), 500);
                        } else {
                            setGeneratingTemplateId(null);
                            setActiveJobId(null);
                            setIsCheckingForImage(false);
                        }
                    });
            };

            checkForImage();
        }
        else if (job.status === 'failed') {
            // Reset on failure
            console.log("Template generation failed:", job.error);
            setGeneratingTemplateId(null);
            setActiveJobId(null);
            setIsCheckingForImage(false);

            // Check if it's a credits error
            const errorMsg = job.error || '';
            if (errorMsg.includes('Out of Credits') || errorMsg.includes('Insufficient Credits')) {
                Swal.fire({
                    title: "<strong>Out Of Credits? Go Unlimited</strong>",
                    html: `<p class="text-base mb-4">You can either top up for online use or download Promptus once to generate offline forever - no monthly fees.</p>
                          <button id="topUpNowBtn" class="rounded-lg bg-yellow py-3 px-5 shadow-xl font-bold text-xl text-white uppercase">Top Up Now</button>
                          <button id="goOfflineBtn" class="rounded-lg bg-red-600 py-3 px-5 shadow-xl font-bold text-xl text-white uppercase mt-3">Go Offline (One-Time Purchase)</button>`,
                    icon: "error",
                    showConfirmButton: false,
                    
                    didOpen: () => {
                        const btn = document.getElementById("topUpNowBtn");
                        const nxtbtn = document.getElementById("goOfflineBtn");
                        if (btn) btn.addEventListener("click", () => openPromptusCreditsStore('payments'));
                        if (nxtbtn) nxtbtn.addEventListener("click", () => openPromptusCreditsStore('subscriptions'));
                    }
                });
            } else {
                Swal.fire({
                    title: 'Generation Failed',
                    text: job.error || 'Failed to generate image. Please try again.',
                    icon: 'error',
                    confirmButtonText: 'OK'
                });
            }
        }
    }, [generateStatus, activeJobId, isCheckingForImage]);

    // Filter templates by category and search query
    const filteredTemplates = templates.filter(template => {
        // Category filter
        const matchesCategory = selectedCategory === 'All' || template.category === selectedCategory;

        // Search filter
        const matchesSearch = searchQuery === '' ||
            template.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            template.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
            template.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
            template.model.toLowerCase().includes(searchQuery.toLowerCase());

        return matchesCategory && matchesSearch;
    });

    const handleTemplateSelect = async (template) => {
        setSelectedTemplate(template);
        setUserPrompt('');
        setUploadedImage(null);
        setImagePreview(null);
        setModelDefaults(null); // Reset previous defaults
        setImageFieldName('input_image'); // Reset to default
        setCosyflowSchema(null); // Reset schema
        setIsModalOpen(true);

        // Fetch model defaults from API
        if (template.model) {
            const result = await fetchModelDefaults(template.model);
            if (result) {
                setModelDefaults(result.defaults);
                setCosyflowSchema(result.schema);
                if (result.imageFieldName) {
                    setImageFieldName(result.imageFieldName);
                }
                console.log('[Templates] Model defaults loaded:', result.defaults);
                console.log('[Templates] Image field name:', result.imageFieldName || 'default');
            } else {
                console.warn('[Templates] Could not load model defaults, using template defaults');
                setImageFieldName('input_image'); // Reset to default
                setCosyflowSchema(null);
            }
        }
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setUploadedImage(file);

            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = () => {
        setUploadedImage(null);
        setImagePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleApplyTemplate = async () => {
        if (!selectedTemplate || !userPrompt.trim()) return;

        // Combine template prompt with user's custom prompt
        const finalPrompt = `${selectedTemplate.prompt}, ${userPrompt}`;

        // Helper function to set nested property using dot notation
        const setNestedProperty = (obj, path, value) => {
            const keys = path.split('.');
            const lastKey = keys.pop();
            const target = keys.reduce((acc, key) => {
                if (!acc[key]) acc[key] = {};
                return acc[key];
            }, obj);
            target[lastKey] = value;
        };

        // Build params object dynamically from model defaults
        // Handle both flat fields and nested fields (with dots)
        const params = {};

        if (modelDefaults && typeof modelDefaults === 'object') {
            try {
                // Process each default, handling dot notation for nesting
                for (const [key, value] of Object.entries(modelDefaults)) {
                    if (key.includes('.')) {
                        // Nested field like "params.width"
                        setNestedProperty(params, key, value);
                    } else {
                        // Flat field like "model"
                        params[key] = value;
                    }
                }
            } catch (error) {
                console.error('[Templates] Error processing model defaults:', error);
            }
        }

        console.log('[Templates] Model defaults loaded:', !!modelDefaults);
        console.log('[Templates] Starting params from model defaults:', params);
        console.log('[Templates] Cosyflow schema available:', !!cosyflowSchema);

        // Find prompt field by TYPE (not name) from schema
        let promptField = null;
        if (cosyflowSchema && typeof cosyflowSchema === 'object') {
            try {
                for (const [key, fieldDef] of Object.entries(cosyflowSchema)) {
                    if (fieldDef && fieldDef.type === 'prompt') {
                        promptField = key;
                        console.log(`[Templates] Found prompt field by type: '${promptField}'`);
                        break;
                    }
                }
            } catch (error) {
                console.error('[Templates] Error finding prompt field in schema:', error);
            }
        }

        if (!promptField) {
            console.warn('[Templates] No prompt field found in schema');
        }

        // Override the prompt field with combined template + user prompt
        if (promptField) {
            if (promptField.includes('.')) {
                setNestedProperty(params, promptField, finalPrompt);
            } else {
                params[promptField] = finalPrompt;
            }
            console.log(`[Templates] Set prompt field '${promptField}' = "${finalPrompt}"`);
        } else {
            console.warn('[Templates] No prompt field found in schema, skipping prompt override');
        }

        // Set the image field dynamically with the uploaded image
        if (imagePreview && imageFieldName) {
            // Strip the data URI prefix (e.g., "data:image/png;base64,") to get just the base64 string
            let base64Image = imagePreview;
            if (imagePreview.includes('base64,')) {
                base64Image = imagePreview.split('base64,')[1];
                console.log('[Templates] Stripped data URI prefix from image');
            }

            if (imageFieldName.includes('.')) {
                setNestedProperty(params, imageFieldName, base64Image);
            } else {
                params[imageFieldName] = base64Image;
            }
            console.log(`[Templates] Set image field '${imageFieldName}' with base64 image (length: ${base64Image.length})`);
        }

        console.log('[Templates] Final params object before count:', params);
        console.log('[Templates] modelDefaults:', modelDefaults);

        // Always add count field at top level (matching PromptHeader logic at line 2491)
        // Check multiple possible count field names from defaults, default to 1
        let countValue = 1; // Default

        if (modelDefaults && typeof modelDefaults === 'object') {
            countValue = modelDefaults.batch_size ||
                        modelDefaults.imageCount ||
                        modelDefaults.count ||
                        modelDefaults.n ||
                        modelDefaults['params.count'] ||
                        modelDefaults['params.n'] ||
                        1;
        }

        // Ensure countValue is a valid number
        if (typeof countValue !== 'number' || countValue < 1) {
            countValue = 1;
        }

        console.log('[Templates] Using count value:', countValue);

        if (!selectedTemplate.model || selectedTemplate.model.trim() === '') {
            Swal.fire({
                title: 'Model Missing',
                text: 'This template does not have a valid model assigned. Please try a different template.',
                icon: 'error',
                confirmButtonColor: '#FFB02E',
            });
            return;
        }

        const promptData = {
            prompt: {
                ...params,  // Spread all params directly into prompt object
                count: countValue,  // Always include count at top level
                cosyflow: selectedTemplate.model  // Add cosyflow field
            }
        };

        console.log('[Templates] Final promptData structure:', JSON.stringify(promptData, null, 2));

        // Close modal immediately for better UX
        handleCloseModal();

        // Set generating state immediately and reset checking flag
        setGeneratingTemplateId(selectedTemplate.id);
        setIsCheckingForImage(false);

        try {
            // Get account number from account context
            const accountNo = accountContext.accountDetails.accountNo;
            if (!accountNo) {
                throw new Error('Account not found. Please login first.');
            }
            promptData.accountNo = accountNo;

            // Get IP address
            let ip = '';
            try {
                const response = await fetch('https://api.ipify.org');
                ip = await response.text();
            } catch (error) {
                console.error('Failed to get IP:', error);
            }

            promptData.ip_address = ip;
            promptData.source = electronBridge.isElectron ? 'PWA' : 'PWeb';
            promptData.version = process.env.REACT_APP_VERSION;

            console.log("Sending template generation with complete data:", promptData);

            // Send the generation request
            const data = await generatePrompt(promptData, apiState, generateStatusDispatch);

            console.log("Template generation job response:", data);

            if (data.error) {
                throw new Error(data.error);
            }

            // Add the job to GenerateContext to show progress
            const jobId = data.uid;
            // Get count from either flat structure or nested params
            const count = promptData.prompt.count ||
                         promptData.prompt.params?.count ||
                         promptData.prompt.params?.n ||
                         promptData.prompt.n ||
                         1;

            console.log('[Templates] Using count for job tracking:', count);

            if (jobId) {
                // Create placeholders BEFORE adding job (critical for proper image tracking)
                const placeholderIds = [];
                for (let i = 0; i < count; i++) {
                    const placeholderHash = md5(String(Math.floor(Math.random() * 1e8)));
                    const id = await db.images.put({
                        image: "",
                        seed: "",
                        uid: jobId,
                        model: selectedTemplate.model,
                        inCollection: false,
                        hash: placeholderHash,
                        online: apiState.online,
                        tag: "",
                        prompt: finalPrompt,
                        requestedtimestamp: Date.now(),
                        receivedtimestamp: Date.now(),
                        dataType: "placeholder"
                    });
                    placeholderIds.push(id);
                }
                console.log(`Created ${count} placeholder(s) for job ${jobId}`, placeholderIds);

                // Now add job to tracking
                generateStatusDispatch({type: 'addJob', uid: jobId, count: count});
                console.log("Job added to GenerateContext:", jobId);

                // Set active job ID
                setActiveJobId(jobId);

                // Trigger image gallery refresh on the Generate page
                // This ensures placeholders show up in the UI
                setTimeout(() => {
                    eventBus.emit('refreshImages');
                }, 100);
            }

            // Show success notification
            Swal.fire({
                title: 'Generation Started!',
                text: 'Your image is being generated. The result will appear shortly.',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });

            // If user opened templates from menu (full page), navigate to playground
            console.log('[TEMPLATES DEBUG] Navigation decision:', {
                pathname: location.pathname,
                isFullPageRoute: location.pathname === '/templates' || location.pathname.startsWith('/templates/'),
                action: location.pathname === '/templates' || location.pathname.startsWith('/templates/') ? 'navigate to /' : 'emit overlay toggle',
                timestamp: new Date().toISOString()
            });

            if (location.pathname === '/templates' || location.pathname.startsWith('/templates/')) {
                console.log('[TEMPLATES DEBUG] Executing navigate("/") for full page route');
                navigate('/');
            } else {
                console.log('[TEMPLATES DEBUG] Executing eventBus.emit("toggle_content_overlay") for overlay route');
                // If using overlay button, just close the overlay
                eventBus.emit('toggle_content_overlay');
            }

        } catch (error) {
            console.error("Template generation error:", error);

            // Reset all state on error
            setGeneratingTemplateId(null);
            setActiveJobId(null);
            setIsCheckingForImage(false);

            // Check if it's a credits error
            const errorMsg = error.message || '';
            if (errorMsg.includes('Out of Credits') || errorMsg.includes('Insufficient Credits')) {
                Swal.fire({
                    title: "<strong>Out Of Credits? Go Unlimited</strong>",
                    html: `<p class="text-base mb-4">You can either top up for online use or download Promptus once to generate offline forever - no monthly fees.</p>
                          <button id="topUpNowBtn" class="rounded-lg bg-yellow py-3 px-5 shadow-xl font-bold text-xl text-white uppercase">Top Up Now</button>
                          <button id="goOfflineBtn" class="rounded-lg bg-red-600 py-3 px-5 shadow-xl font-bold text-xl text-white uppercase mt-3">Go Offline (One-Time Purchase)</button>`,
                    icon: "error",
                    showConfirmButton: false,
                    
                    didOpen: () => {
                        const btn = document.getElementById("topUpNowBtn");
                        const nxtbtn = document.getElementById("goOfflineBtn");
                        if (btn) btn.addEventListener("click", () => openPromptusCreditsStore('payments'));
                        if (nxtbtn) nxtbtn.addEventListener("click", () => openPromptusCreditsStore('subscriptions'));
                    }
                });
            } else {
                setPendingTemplateId(selectedTemplate?.id);
                setShowLoginModal(true)
            }
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedTemplate(null);
        setUserPrompt('');
        setUploadedImage(null);
        setImagePreview(null);
        setModelDefaults(null);
        setImageFieldName('input_image'); // Reset to default
        setCosyflowSchema(null); // Reset schema

        // If we came from a URL with templateId, navigate back to base templates route
        if (templateId) {
            navigate('/templates');
        }
    };

    return (
        <div className="px-4 relative container mx-auto pb-8 bg-dark-blue">
            <LoginModal
                isOpen={showLoginModal}
                onClose={() => setShowLoginModal(false)}
                onLoginSuccess={(userData) => {
                    console.log('User logged in:', userData);
                    setShowLoginModal(false);
                    if (pendingTemplateId) {
                        navigate(`/templates/${pendingTemplateId}`);
                        setPendingTemplateId(null);
                    }
                }}
                promptMessage="Please log in to start generating"
            />
            <h2 className="text-xl py-2 font-bold flex flex-row items-center">
                <span>Templates</span>
                <hr className="ml-2 flex-grow border border-white/40 bg-white/40" />
            </h2>

            {/* Error message */}
            {fetchError && (
                <div className="mt-4 bg-yellow/20 border border-yellow/50 rounded-lg p-3 text-white">
                    <p className="text-sm">{fetchError}</p>
                </div>
            )}

            {/* Loading state */}
            {(isLoadingTemplates || isLoadingCategories) && (
                <div className="mt-8 text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-yellow"></div>
                    <p className="text-white mt-4">Loading templates...</p>
                </div>
            )}

            {/* Search Bar */}
            {!isLoadingTemplates && !isLoadingCategories && (
                <div className="mt-4 mb-4">
                <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search templates by name, description, category, or model..."
                    className="w-full bg-dark-blue/30 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:border-cyan focus:outline-none text-base"
                />
                </div>
            )}

            {/* Category Filter */}
            {!isLoadingTemplates && !isLoadingCategories && (
                <div className="flex flex-wrap gap-2 mb-6">
                {categories.map(category => (
                    <button
                        key={category}
                        onClick={() => setSelectedCategory(category)}
                        className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 ${
                            selectedCategory === category
                                ? 'bg-yellow text-dark-blue'
                                : 'bg-dark-blue/30 text-white hover:bg-dark-blue/50 border border-white/20'
                        }`}
                    >
                        {category}
                    </button>
                    ))}
                </div>
            )}

            {/* Templates Grid */}
            {!isLoadingTemplates && !isLoadingCategories && filteredTemplates.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredTemplates.map(template => (
                        <TemplateCard
                            key={template.id}
                            template={template}
                            onSelect={handleTemplateSelect}
                            isGenerating={generatingTemplateId === template.id}
                        />
                        ))}
                    </div>
                ) : !isLoadingTemplates && !isLoadingCategories ? (
                <div className="text-center py-12">
                    <p className="text-white/60 text-lg">No templates found matching your search.</p>
                    <button
                        onClick={() => {
                            setSearchQuery('');
                            setSelectedCategory('All');
                        }}
                        className="mt-4 bg-yellow text-dark-blue px-6 py-2 rounded-lg font-semibold hover:bg-yellow/90 transition-all"
                    >
                        Clear Filters
                        </button>
                    </div>
                ) : null}

            {/* Template Customization Modal */}
            <Modal
                open={isModalOpen}
                onClose={handleCloseModal}
                center
                container={document.querySelector('.main-content') || undefined}
                closeIcon={<CloseIcon className="fill-white" />}
                classNames={{
                    modal: 'customModal',
                    overlay: 'customOverlay'
                }}
                styles={{
                    modal: {
                        background: '#190A4E',
                        border: '2px solid rgba(0, 217, 255, 0.5)',
                        borderRadius: '12px',
                        padding: 0,
                        maxWidth: '48rem',
                        width: '90%',
                        maxHeight: '85vh',
                        overflowY: 'auto'
                    },
                    overlay: {
                        background: 'rgba(0, 0, 0, 0.8)'
                    },
                    closeButton: {
                        width: '24px',
                        height: '24px',
                        top: '12px',
                        right: '12px'
                    }
                }}
            >
                {selectedTemplate && (
                    <div className="p-6">
                        {/* Modal Header */}
                        <div className="mb-6">
                            <h2 className="text-2xl font-bold text-white mb-2">{selectedTemplate.title}</h2>
                            <p className="text-base text-white/80 mb-4">{selectedTemplate.description}</p>

                            {/* Template Info */}
                            <div className="bg-dark-blue/50 p-4 rounded-lg mb-4">
                                {isLoadingModelDefaults ? (
                                    <div className="flex items-center justify-center py-4">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan mr-3"></div>
                                        <span className="text-cyan text-sm">Loading model defaults...</span>
                                    </div>
                                ) : (
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-cyan font-semibold">Model:</span>
                                            <span className="text-white/80">{selectedTemplate.model}</span>
                                        </div>
                                        {modelDefaults ? (
                                            <>
                                                <div className="pt-2 border-t border-white/10">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <span className="text-yellow text-xs">✓ Using API defaults</span>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        {Object.entries(modelDefaults)
                                                            .filter(([key]) =>
                                                                // Filter out image fields, prompts, and empty values
                                                                !key.toLowerCase().includes('image') &&
                                                                !key.toLowerCase().includes('prompt') &&
                                                                modelDefaults[key] !== '' &&
                                                                modelDefaults[key] !== null
                                                            )
                                                            .slice(0, 6) // Limit to first 6 parameters for clean display
                                                            .map(([key, value]) => (
                                                                <div key={key} className="flex justify-between text-xs">
                                                                    <span className="text-cyan capitalize">{key.replace(/_/g, ' ')}:</span>
                                                                    <span className="text-white/80 truncate ml-2 max-w-[100px]" title={String(value)}>
                                                                        {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-white/10">
                                                <div className="flex justify-between text-xs">
                                                    <span className="text-cyan">Steps:</span>
                                                    <span className="text-white/80">{selectedTemplate.parameters.steps}</span>
                                                </div>
                                                <div className="flex justify-between text-xs">
                                                    <span className="text-cyan">Guidance:</span>
                                                    <span className="text-white/80">{selectedTemplate.parameters.guidance}</span>
                                                </div>
                                                <div className="flex justify-between text-xs">
                                                    <span className="text-cyan">Size:</span>
                                                    <span className="text-white/80">{selectedTemplate.parameters.imageWidth}x{selectedTemplate.parameters.imageHeight}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="bg-yellow/10 border border-yellow/30 p-3 rounded-lg">
                                <p className="text-sm text-white/90">
                                    <span className="font-semibold text-yellow">Base Prompt:</span> {selectedTemplate.prompt}
                                </p>
                            </div>
                        </div>

                        {/* Custom Prompt Input */}
                        <div className="mb-6">
                            <label className="block text-white font-semibold mb-2 text-base">
                                Your Prompt <span className="text-red-400">*</span>
                            </label>
                            <textarea
                                value={userPrompt}
                                onChange={(e) => setUserPrompt(e.target.value)}
                                placeholder="Enter your main prompt here... (e.g., 'a woman with red hair standing at sunset', 'cyberpunk city at night', 'ancient temple in the jungle')"
                                className="w-full bg-dark-blue/30 border border-white/20 rounded-lg p-4 text-white placeholder-white/40 focus:border-cyan focus:outline-none min-h-[120px] text-base resize-none"
                            />
                            <p className="text-sm text-white/60 mt-2">
                                This will be combined with the template's optimized settings for best results.
                            </p>
                        </div>

                        {/* Image Upload - Only show if template has_image is true */}
                        {selectedTemplate.has_image && (
                            <div className="mb-6">
                                <label className="block text-white font-semibold mb-2 text-base">
                                    Upload Reference Image (Optional)
                                </label>

                                {imagePreview ? (
                                    <div className="relative bg-dark-blue/30 border border-white/20 rounded-lg p-4">
                                        <img
                                            src={imagePreview}
                                            alt="Preview"
                                            className="max-h-48 mx-auto rounded-lg"
                                        />
                                        <button
                                            onClick={handleRemoveImage}
                                            className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-2 transition-colors"
                                        >
                                            <CloseIcon className="w-4 h-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <div
                                        onClick={() => fileInputRef.current?.click()}
                                        className="border-2 border-dashed border-white/20 rounded-lg p-8 text-center cursor-pointer hover:border-cyan/50 hover:bg-dark-blue/20 transition-all"
                                    >
                                        <div className="text-white/60 mb-2">
                                            <UploadIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                            <p className="text-base">Click to upload an image</p>
                                            <p className="text-sm text-white/40 mt-1">PNG, JPG up to 10MB</p>
                                        </div>
                                    </div>
                                )}

                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    className="hidden"
                                />
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex gap-3">
                            <button
                                onClick={handleCloseModal}
                                className="flex-1 bg-dark-blue/50 hover:bg-dark-blue/70 text-white font-bold py-3 px-4 rounded-lg transition-all border border-white/20 text-base"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleApplyTemplate}
                                disabled={!userPrompt.trim()}
                                className="flex-1 bg-yellow hover:bg-yellow/90 text-dark-blue font-bold py-3 px-4 rounded-lg transition-all text-base disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Generate
                            </button>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Generated Image Modal */}
            <Modal
                open={showImageModal}
                onClose={() => {
                    console.log("Closing image modal");
                    setShowImageModal(false);
                }}
                center
                container={portalContainer || undefined}
                closeIcon={<CloseIcon className="fill-white" />}
                styles={{
                    modal: {
                        background: '#190A4E',
                        border: '2px solid rgba(0, 217, 255, 0.5)',
                        borderRadius: '12px',
                        padding: '1.5rem',
                        maxWidth: '56rem',
                        width: '90%',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        zIndex: 10002
                    },
                    overlay: {
                        background: 'rgba(0, 0, 0, 0.8)',
                        zIndex: 10001
                    },
                    closeButton: {
                        width: '24px',
                        height: '24px',
                        top: '12px',
                        right: '12px'
                    }
                }}
            >
                {generatedImage && (
                    <div className="text-center">
                        <h2 className="text-2xl font-bold text-white mb-4">Your Generated Image</h2>
                        <img
                            src={generatedImage.image?.startsWith('data:') ? generatedImage.image : `data:image/webp;base64,${generatedImage.image}`}
                            alt="Generated"
                            className="max-w-full h-auto rounded-lg mb-4 mx-auto"
                            onLoad={() => console.log("Image loaded successfully")}
                            onError={(e) => console.error("Image failed to load:", e)}
                        />
                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={() => setShowImageModal(false)}
                                className="bg-dark-blue/50 hover:bg-dark-blue/70 text-white font-bold py-3 px-6 rounded-lg transition-all border border-white/20"
                            >
                                Close
                            </button>
                            <button
                                onClick={() => {
                                    setShowImageModal(false);
                                    navigate('/');
                                }}
                                className="bg-yellow hover:bg-yellow/90 text-dark-blue font-bold py-3 px-6 rounded-lg transition-all"
                            >
                                Go to Playground
                            </button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}
